import type { ActiveSlugsData } from "@/modules/jobSearchPrefiltered/types"

import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "next/navigation"
import { useContext } from "react"

import {
  GET_ACTIVE_SLUGS_QUERY_KEY,
  LIST_ITEMS_PER_PAGE,
  QUERY_CACHE_TIME,
  QUERY_STALE_TIME,
} from "@/modules/jobSearchPrefiltered/constants"
import { getActiveSlugs } from "@/modules/jobSearchPrefiltered/services/getActiveSlugs"
import { useLocale } from "@/modules/locales/useLocale"
import { ServerContext } from "@/modules/server/context"

export type ActiveSlugsFetch = {
  dataActiveSlugs?: ActiveSlugsData
  isLoadingActiveSlugs: boolean
  isSuccessActiveSlugs: boolean
  isErrorActiveSlugs: boolean
}

export const useActiveSlugsFetch = (): ActiveSlugsFetch => {
  const context = useContext(ServerContext) || { hostname: "" }
  const locale = useLocale()
  const searchParams = useSearchParams()
  const currentPage = searchParams.has("page") ? Number(searchParams.get("page")) : 1

  if (!context || !context?.hostname) {
    throw new Error(`Missing hostname when retrieving active slugs data.`)
  }

  const { hostname } = context

  const {
    data: dataActiveSlugs,
    isPending: isLoadingActiveSlugs,
    isSuccess: isSuccessActiveSlugs,
    isError: isErrorActiveSlugs,
  } = useQuery({
    gcTime: QUERY_CACHE_TIME,

    placeholderData: previousData => previousData,

    queryFn: () =>
      getActiveSlugs({
        hostname,
        locale,
        params: { page: `${currentPage}`, page_size: `${LIST_ITEMS_PER_PAGE}` },
      }),
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [GET_ACTIVE_SLUGS_QUERY_KEY, locale, currentPage],
    staleTime: QUERY_STALE_TIME,
  })

  return {
    dataActiveSlugs,
    isErrorActiveSlugs,
    isLoadingActiveSlugs,
    isSuccessActiveSlugs,
  }
}
